import { Map } from 'immutable'
import { getAnonymousUuid } from '../../browser/anonymous/index'
import { isProduction } from '../../environment/index'
import {
  canSetAffiliateAttributionCookie,
  getCookie,
  setCookie,
} from '../cookie/index'
import {
  getAuthIsLoggedIn,
  getAuthUid,
} from '../auth/index'
import { hasEverflowTransactionCookie } from '../everflow/transaction-cookie'
import { setAnonymousUuid } from '../user/anonymous-uuid-actions'

export const IMPACT_IRCLICKID_COOKIE_NAME = 'gaia_impact_irclickid'
export const IMPACT_IRCLICKID_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60

export function isIreAvailable () {
  return typeof global.ire === 'function'
}

/**
 * Persist Impact click id from landing URL (im_ref).
 * @param {string} clickId
 * @param {Object} [options]
 * @param {number} [options.uid]
 * @param {string} [options.authToken]
 * @param {import('immutable').Map} [options.auth] Preferred for compliance checks
 */
export function setImpactIrclickidCookie (clickId, options = {}) {
  if (!clickId) {
    return
  }
  const { uid, authToken, auth } = options
  const authMap = Map.isMap(auth)
    ? auth
    : Map({ uid, jwt: authToken })
  if (!canSetAffiliateAttributionCookie(authMap)) {
    return
  }
  setCookie(
    IMPACT_IRCLICKID_COOKIE_NAME,
    String(clickId),
    '/',
    IMPACT_IRCLICKID_COOKIE_MAX_AGE_SECONDS,
    isProduction(),
    uid,
    authToken,
  )
}

/**
 * @returns {string|undefined}
 */
export function getImpactIrclickidCookie () {
  const value = getCookie(IMPACT_IRCLICKID_COOKIE_NAME)
  return value || undefined
}

/**
 * Impact click id for checkout / eventLog: omitted when Everflow transaction cookie is set;
 * otherwise cookie first, then session inboundTracking Redux.
 * @param {import('immutable').Map} [inboundTracking]
 * @returns {string|undefined}
 */
export function resolveImpactIrclickidForTracking (inboundTracking) {
  if (hasEverflowTransactionCookie()) {
    return undefined
  }
  const fromCookie = getImpactIrclickidCookie()
  if (fromCookie) {
    return fromCookie
  }
  const inboundMap = Map.isMap(inboundTracking) ? inboundTracking : Map()
  const fromRedux = inboundMap.getIn(['data', 'irclickid'])
  if (fromRedux !== undefined && fromRedux !== null && String(fromRedux) !== '') {
    return String(fromRedux)
  }
  return undefined
}

/**
 * @param {import('immutable').Map} [query] Resolver query
 * @returns {string|undefined}
 */
export function getImRefFromResolverQuery (query) {
  const queryMap = Map.isMap(query) ? query : Map()
  const imRef = queryMap.get('im_ref')
  if (imRef === undefined || imRef === null || String(imRef) === '') {
    return undefined
  }
  return String(imRef)
}

/**
 * Set Impact click-id cookie when resolver query includes im_ref.
 * @param {{ auth?: import('immutable').Map, resolver?: import('immutable').Map }} state
 */
export function syncImpactImRefCookieFromState (state) {
  const auth = Map.isMap(state?.auth) ? state.auth : Map()
  const resolver = Map.isMap(state?.resolver) ? state.resolver : Map()
  const imRef = getImRefFromResolverQuery(resolver.get('query'))
  if (!imRef) {
    return
  }
  setImpactIrclickidCookie(imRef, { auth })
}

/**
 * @param {{ state?: { resolver?: import('immutable').Map } }} context
 * @returns {boolean}
 */
export function stateResolverQueryHasImRef ({ state } = {}) {
  const resolver = Map.isMap(state?.resolver) ? state.resolver : Map()
  return Boolean(getImRefFromResolverQuery(resolver.get('query')))
}

/**
 * Impact customProfileId: stable anonymous device id (same anon → logged in).
 * @param {import('immutable').Map} [user] Redux user state
 * @param {Object} [options]
 * @param {import('redux').Dispatch} [options.dispatch] When set, persists API-fetched uuid to Redux
 * @returns {Promise<string>}
 */
export async function resolveImpactCustomProfileId (user, options = {}) {
  const { dispatch } = options
  const userMap = Map.isMap(user) ? user : Map()
  const fromRedux = userMap.getIn(['data', 'anonymousUuid'])
  if (fromRedux !== undefined && fromRedux !== null && String(fromRedux) !== '') {
    return String(fromRedux)
  }
  const fromApi = await getAnonymousUuid()
  const customProfileId = fromApi ? String(fromApi) : ''
  if (customProfileId && typeof dispatch === 'function') {
    dispatch(setAnonymousUuid(customProfileId))
  }
  return customProfileId
}

async function sha1Hex (text) {
  const data = new TextEncoder().encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-1', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Build Impact identify payload per impact.com spec (empty strings when unknown).
 * @param {Object} options
 * @param {import('immutable').Map} [options.auth] Redux auth state
 * @param {import('immutable').Map} [options.user] Redux user state
 * @param {import('redux').Dispatch} [options.dispatch]
 * @returns {Promise<{ customerId: string, customerEmail: string, customProfileId: string }>}
 */
export async function buildImpactIdentifyPayload (options = {}) {
  const { auth, user, dispatch } = options
  const authMap = Map.isMap(auth) ? auth : Map()
  const customProfileId = await resolveImpactCustomProfileId(user, { dispatch })

  if (!getAuthIsLoggedIn(authMap)) {
    return {
      customerId: '',
      customerEmail: '',
      customProfileId,
    }
  }

  const uid = getAuthUid(authMap)
  const customerId = uid !== undefined && uid !== null ? String(uid) : ''
  const email = authMap.get('email', '')
  let customerEmail = ''
  if (typeof email === 'string' && email.trim()) {
    customerEmail = await sha1Hex(email.trim().toLowerCase())
  }

  return {
    customerId,
    customerEmail,
    customProfileId,
  }
}

/**
 * Invoke ire('identify', ...) when the Universal Tracking Tag stub is present.
 * @param {Object} options
 * @param {import('immutable').Map} [options.auth] Redux auth state
 * @param {import('immutable').Map} [options.user] Redux user state
 * @param {import('redux').Dispatch} [options.dispatch]
 */
export async function invokeImpactIdentify (options = {}) {
  if (!isIreAvailable()) {
    return
  }
  const payload = await buildImpactIdentifyPayload(options)
  global.ire('identify', payload)
}
